import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { ProgramType } from "../../../domain/treatmentPlan.interface";

interface ProgramTypeSelectorProps {
	value: ProgramType;
	onChange: (type: ProgramType) => void;
	disabled?: boolean;
}

const options: Array<{
	type: ProgramType;
	label: string;
	description: string;
	icon: React.ReactNode;
	badge?: string;
}> = [
	{
		type: "WEEKLY_CALENDAR",
		label: "Weekly Calendar",
		description:
			"Manually configure behaviors, rewards, visit schedules, and treatment plan options.",
		icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
	},
	{
		type: "SMART_PROGRAM",
		label: "Smart Program",
		description:
			"AI-powered workflow configuration. Describe your program in plain English and get a validated config.",
		icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
		badge: "AI-Powered",
	},
];

const ProgramTypeSelector: React.FC<ProgramTypeSelectorProps> = ({
	value,
	onChange,
	disabled,
}) => {
	return (
		<Box sx={{ mb: 3 }}>
			<Typography variant="subtitle2" sx={{ mb: 1 }}>
				Program Type
			</Typography>
			<Box sx={{ display: "flex", gap: 2 }}>
				{options.map((option) => {
					const isSelected = value === option.type;
					return (
						<Card
							key={option.type}
							onClick={() => !disabled && onChange(option.type)}
							sx={{
								flex: 1,
								cursor: disabled ? "default" : "pointer",
								border: 2,
								borderColor: isSelected ? "primary.main" : "grey.300",
								opacity: disabled ? 0.6 : 1,
								transition: "border-color 0.2s",
								"&:hover": disabled ? {} : { borderColor: "primary.light" },
							}}
						>
							<CardContent
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									textAlign: "center",
									gap: 1,
								}}
							>
								<Box
									sx={{
										color: isSelected ? "primary.main" : "text.secondary",
									}}
								>
									{option.icon}
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1,
									}}
								>
									<Typography variant="subtitle1" fontWeight="bold">
										{option.label}
									</Typography>
									{option.badge && (
										<Chip label={option.badge} size="small" color="secondary" />
									)}
								</Box>
								<Typography variant="body2" color="text.secondary">
									{option.description}
								</Typography>
							</CardContent>
						</Card>
					);
				})}
			</Box>
		</Box>
	);
};

export default ProgramTypeSelector;
